import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Icon from "@mui/material/Icon"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import {
  useGetGroupOrderQuery,
  useSubmitOrderActionMutation,
  usePartialApproveGroupOrderMutation,
} from "shared/redux/endpoints/procurementApprovals"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import StatusBadge from "./components/StatusBadge"
import GroupOrderCard from "./components/orders/GroupOrderCard"
import OrderDetailModal from "./components/orders/OrderDetailModal"
import { ConfirmModal } from "./components/approvals/DecisionCard"
import PartialApproveModal from "./components/orders/PartialApproveModal"
import { ORDER_STATUS } from "./status"
import { getApiErrorMessage } from "./orderSubmitFeedback"
import { isAutoRejectedOutcome, AUTO_REJECT_MESSAGE } from "./actionResultFeedback"
import { approvalLimitExceededMessage, formatVND } from "./constants"

// Same preconditions the backend enforces on partial-approve (409 otherwise).
const partialApproveBlockedReason = (group) => {
  if (group.status === "closed") return "This group is closed."
  const members = group.orders || []
  const approved = members.filter((o) => o.status === ORDER_STATUS.APPROVED).length
  if (approved === 0) return "Needs at least 1 approved order in the group."
  if (approved === members.length) return "All orders are already approved — nothing to split."
  return null
}

const GroupOrderDetailContainer = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: group, isLoading, isError, refetch } = useGetGroupOrderQuery(id, { skip: !id })
  const [submitAction] = useSubmitOrderActionMutation()
  const [partialApprove, { isLoading: partialApproving }] = usePartialApproveGroupOrderMutation()
  const [picked, setPicked] = useState(null)
  const [busy, setBusy] = useState(false)
  const [detailId, setDetailId] = useState(null)
  const [partialOpen, setPartialOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [bulkOpen, setBulkOpen] = useState(false)

  const toggleSelect = (orderId, checked) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(orderId)
      else next.delete(orderId)
      return next
    })

  // One call per order — no batch endpoint. Each card's checkbox is only
  // enabled when that order's allowedActions include "approve".
  const runBulkApprove = async (note) => {
    const ids = [...selectedIds]
    setBusy(true)
    const results = await Promise.allSettled(
      ids.map((orderId) => submitAction({ id: orderId, action: "approve", note }).unwrap())
    )
    const ok = results.filter((r) => r.status === "fulfilled").length
    const failed = results.filter((r) => r.status === "rejected")
    if (ok) showToastSuccess(ok === 1 ? "Your decision was submitted." : `${ok} orders approved.`)
    if (failed.length) {
      showToastError(getApiErrorMessage(failed[0].reason, `${failed.length} order(s) could not be approved.`))
    }
    setBusy(false)
    setBulkOpen(false)
    setSelectedIds(new Set())
    refetch()
  }

  const onConfirm = async (note, deadline) => {
    if (busy || !picked) return
    setBusy(true)
    try {
      const result = await submitAction({ id: picked.order.id, action: picked.action, note, deadline }).unwrap()
      if (isAutoRejectedOutcome(picked.action, result, ORDER_STATUS.REJECTED)) {
        showToastError(AUTO_REJECT_MESSAGE)
      } else {
        showToastSuccess("Your decision was submitted.")
      }
      setPicked(null)
      refetch()
    } catch (e) {
      const msg = e?.data?.data?.message || e?.data?.message
      if (e?.status === 409) {
        showToastError("This order was already updated.")
        setPicked(null)
        refetch()
      } else if (e?.status === 403) {
        showToastError(approvalLimitExceededMessage(msg) || "You're not allowed to act on this order.")
        setPicked(null)
      } else {
        showToastError(msg || "Couldn't submit your decision. Please try again.")
      }
    } finally {
      setBusy(false)
    }
  }

  const runPartialApprove = async () => {
    try {
      const result = await partialApprove(group.id).unwrap()
      const detached = result?.detachedOrders || []
      showToastSuccess(
        detached.length
          ? `Split ${detached.map((o) => o.code).join(", ")} out of ${group.code} — they'll keep going through approval on their own.`
          : `${group.code} is already fully approved — nothing to split.`
      )
    } catch (e) {
      showToastError(getApiErrorMessage(e, `Couldn't split ${group.code}.`))
    } finally {
      setPartialOpen(false)
    }
  }

  const back = (
    <MDButton variant="text" color="info" size="small" onClick={() => navigate("/procurement/orders/groups")}>
      <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
      Back
    </MDButton>
  )

  if (isLoading || isError || !group) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <MDBox mb={2}>{back}</MDBox>
          <MDTypography variant="body2" color="text">
            {isLoading ? "Loading…" : "Couldn't load this group order."}
          </MDTypography>
          {isError && (
            <MDButton variant="outlined" color="info" size="small" onClick={refetch} sx={{ mt: 1 }}>
              Retry
            </MDButton>
          )}
        </MDBox>
      </DashboardLayout>
    )
  }

  const orders = group.orders || []
  const blockedReason = partialApproveBlockedReason(group)

  return (
    <DashboardLayout>
      <MDBox p={3}>
        <MDBox mb={2}>{back}</MDBox>

        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5} mb={3}>
          <MDBox>
            <MDTypography variant="h4">{group.code}</MDTypography>
            <MDTypography variant="body2" color="text">
              {group.title} · {orders.length} orders · {formatVND(group.totalAmount ?? group.total)}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <StatusBadge status={group.status} />
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={() => navigate(`/procurement/orders/groups/${group.id ?? id}/vendor-proposals`)}
            >
              Vendor proposals
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              size="small"
              disabled={selectedIds.size === 0 || busy}
              onClick={() => setBulkOpen(true)}
            >
              {selectedIds.size ? `Approve selected (${selectedIds.size})` : "Approve selected"}
            </MDButton>
            <span title={blockedReason || undefined}>
              <MDButton
                variant="gradient"
                color="warning"
                size="small"
                disabled={!!blockedReason || partialApproving}
                onClick={() => setPartialOpen(true)}
              >
                Partial approve
              </MDButton>
            </span>
          </MDBox>
        </MDBox>

        {orders.length === 0 ? (
          <MDTypography variant="body2" color="text">
            No orders in this group.
          </MDTypography>
        ) : (
          orders.map((o) => (
            <GroupOrderCard
              key={o.id}
              orderId={o.id}
              onPickAction={(order, action) => setPicked({ order, action })}
              onViewDetail={setDetailId}
              selected={selectedIds.has(o.id)}
              onToggleSelect={toggleSelect}
            />
          ))
        )}
      </MDBox>

      {picked && (
        <ConfirmModal
          actionKey={picked.action}
          order={picked.order}
          busy={busy}
          onCancel={() => !busy && setPicked(null)}
          onSubmit={onConfirm}
        />
      )}
      {bulkOpen && (
        <ConfirmModal
          actionKey="approve"
          order={{
            total: orders.filter((o) => selectedIds.has(o.id)).reduce((sum, o) => sum + (Number(o.totalAmount ?? o.total) || 0), 0),
            categoryName: `${selectedIds.size} selected order${selectedIds.size === 1 ? "" : "s"}`,
          }}
          busy={busy}
          onCancel={() => !busy && setBulkOpen(false)}
          onSubmit={runBulkApprove}
        />
      )}
      {partialOpen && (
        <PartialApproveModal
          group={{ ...group, orCode: group.code }}
          busy={partialApproving}
          onCancel={() => setPartialOpen(false)}
          onSubmit={runPartialApprove}
        />
      )}
      <OrderDetailModal orderId={detailId} onClose={() => setDetailId(null)} />
    </DashboardLayout>
  )
}

export default GroupOrderDetailContainer
